<script setup lang="ts">
import type { TimelineItem } from "@nuxt/ui"
import {
  PhArrowSquareOut,
  PhBracketsCurly,
  PhCloud,
  PhDatabase,
  PhDeviceMobile,
  PhGraph,
  PhPlugsConnected,
  PhUser,
  PhWarning
} from "@phosphor-icons/vue"
import type { Component } from "vue"
import ChartPanel from "../components/ChartPanel.vue"
import DashboardCard from "../components/DashboardCard.vue"
import DashboardBadge from "../components/DashboardBadge.vue"
import DocumentationCallout from "../components/DocumentationCallout.vue"
import PageHeader from "../components/PageHeader.vue"
import DashboardLayout from "../layouts/DashboardLayout.vue"

type ConceptTone = "success" | "warning" | "danger" | "neutral"

interface ConceptCard {
  title: string
  icon?: Component
  tone: ConceptTone
  body: string
  details: string[]
}

interface ReadingLink {
  href: string
  label: string
  note: string
  source: string
}

interface SessionTimelineItem extends TimelineItem {
  actor: string
  detail: string
  handoff: string
  order: string
}

interface TransportDetail {
  stage: string
  flow: string
  implementation: string
  notes: string[]
}

interface CurrentTransportStep {
  actor: string
  bytes: string
  control: string
  evidence: string
  order: string
  title: string
}

interface DiagramHotspot {
  align?: "center" | "end" | "start"
  details: string[]
  eyebrow: string
  height: number
  id: string
  label: string
  side?: "bottom" | "left" | "right" | "top"
  summary: string
  title: string
  tone?: "control" | "data" | "warning"
  width: number
  x: number
  y: number
}

const concepts: ConceptCard[] = [
  {
    title: "Customer request",
    icon: PhUser,
    tone: "neutral",
    body: "The request starts outside our system. A customer sends proxy traffic through a local browser client or the public SOCKS gateway.",
    details: [
      "The customer does not pick an Android device directly.",
      "Proxy-account modifiers such as country, session, strict, and optional non-Android OS profile become broker inputs."
    ]
  },
  {
    title: "Control plane",
    icon: PhCloud,
    tone: "success",
    body: "The Worker API owns session creation, Android node registration, broker assignment, and relay coordinates.",
    details: [
      "Android creates a parent API session and registers capacity.",
      "Browser/gateway broker requests return the selected Android session and relay transport descriptor.",
      "Selection uses active, selectable node registrations; relay health is visibility evidence, not payload handling.",
      "It does not forward the customer's payload bytes."
    ]
  },
  {
    title: "Dataplane",
    tone: "success",
    body: "The relay and Android SDK move opaque traffic after the control plane has made an assignment.",
    details: [
      "Browser TCP uses HTTP/3 CONNECT over QUIC.",
      "Browser UDP uses MASQUE CONNECT-UDP with HTTP datagrams or repo-private datagram capsules.",
      "The relay validates participants and pairs flows by sessionId, but Android owns the destination sockets.",
      "Payload contents stay outside Worker and dashboard records."
    ]
  },
  {
    title: "Browser edge",
    tone: "success",
    body: "The browser-side edge is either proxy-client, which can launch Chrome, or proxy-gateway, which accepts public SOCKS5 clients.",
    details: [
      "Proxy-aware Chrome traffic enters the local SOCKS listener in proxy-client.",
      "TUN mode captures proxy-bypassing TCP/UDP on udtun0 and bridges flows back to the parent MASQUE client.",
      "The public gateway brokers from the SOCKS username and can reuse idle brokered MASQUE clients."
    ]
  },
  {
    title: "Android SDK node",
    tone: "warning",
    body: "The SDK keeps the Android relay participant open, receives flow metadata, opens the target socket from Android, and streams bytes back over the relay.",
    details: [
      "TCP flows use native Android TCP sockets.",
      "UDP flows use target-specific connected UDP sockets in the native runtime.",
      "The current policy accepts new client work only while charging."
    ]
  },
  {
    title: "Session credentials",
    tone: "neutral",
    body: "Credentials are short lived and scoped to a session. Refresh failure closes or pauses affected client work instead of silently continuing.",
    details: ["The SDK avoids long-term secret storage.", "Credential refresh emits structured events."]
  },
  {
    title: "Observability",
    tone: "danger",
    body: "Events describe state changes and errors. They should explain operation without logging payloads, raw credentials, or user identifiers.",
    details: [
      "The dashboard shows allowlisted event records.",
      "Transient upload failure must not block active client connections."
    ]
  }
]

const currentTransportSteps: CurrentTransportStep[] = [
  {
    actor: "Android SDK",
    bytes: "No customer payload is on the path yet.",
    control:
      "ProxySdk.start first checks charging policy, creates POST /v1/sessions, and stores the short-lived credential envelope.",
    evidence: "A non-charging device refuses startup. Credential failure fails closed until the next successful sync.",
    order: "01",
    title: "Bootstrap the Android session"
  },
  {
    actor: "Android SDK + relay",
    bytes: "The gomobile runtime dials the relay over QUIC/HTTP/3 and keeps an outbound undetect-control stream open.",
    control:
      "POST /v1/sessions/:sessionId/nodes/register records device id, nodeCategory, capacity, acceptingClients, and returns type relay/protocol masque transport.",
    evidence: "Kotlin rejects unsupported transport types before starting the native runtime.",
    order: "02",
    title: "Register capacity and join relay"
  },
  {
    actor: "Browser edge + Worker",
    bytes: "Still no target payload; this is assignment metadata only.",
    control:
      "proxy-client can use an explicit --session-id. Otherwise proxy-client or proxy-gateway POSTs /v1/broker/assignments with parsed account, country, session, strict, and OS modifiers.",
    evidence:
      "Eligible nodes must be active, unexpired, accepting, and under capacity. Sticky strict requests fail when the previous node is unavailable.",
    order: "03",
    title: "Resolve the Android session"
  },
  {
    actor: "Browser edge",
    bytes:
      "SOCKS CONNECT becomes an HTTP/3 CONNECT stream. SOCKS UDP ASSOCIATE lazily opens one CONNECT-UDP flow per UDP target.",
    control: "The browser-role MASQUE client sends session id, relay token, and participant id headers to the relay.",
    evidence:
      "proxy-gateway can pool idle brokered MASQUE clients by assignment key; proxy-client owns one browser-side relay client for the launched session.",
    order: "04",
    title: "Normalize proxy traffic into MASQUE"
  },
  {
    actor: "Relay",
    bytes: "Opaque bytes and datagrams move only after Android opens the paired flow stream.",
    control:
      "The relay validates the token, allocates a flowId, writes length-prefixed open metadata to Android control, and waits for /android/flow.",
    evidence:
      "Pairing can fail as android_unpaired, relay flow-limit exceeded, android_target_rejected, browser_context_closed, or android_flow_open_timed_out.",
    order: "05",
    title: "Pair browser and Android flow"
  },
  {
    actor: "Android native runtime",
    bytes:
      "Android dials the target TCP socket or connected UDP socket, then pumps responses back through relay and browser edge.",
    control: "Android opens undetect-tcp or undetect-udp extended CONNECT streams for the relay-provided flowId.",
    evidence:
      "Events report channel type, target connect latency, byte/datagram counts, and close reason without payload contents.",
    order: "06",
    title: "Open the destination from Android"
  },
  {
    actor: "SDK + Worker",
    bytes: "Existing flows drain; new relay opens are skipped while the node is not accepting work.",
    control:
      "Charging, capacity, credential refresh, stale-channel sweeps, and relay-control reconnects update setAcceptingNewClients and node registration state.",
    evidence:
      "The SDK reports active, disconnecting, or disconnected state and restarts the relay generation after control-stream loss.",
    order: "07",
    title: "Maintain admission and recovery"
  }
]

const architectureHotspots: DiagramHotspot[] = [
  {
    align: "start",
    details: [
      "Creates and refreshes parent API credentials.",
      "Stores node registrations, sticky state, and dashboard projections.",
      "Does not forward customer payload bytes."
    ],
    eyebrow: "Decision layer",
    height: 34,
    id: "architecture-control-plane",
    label: "Control plane",
    side: "bottom",
    summary: "Worker API plus KV/D1 records decide which node and relay a browser side should use.",
    title: "Control plane",
    tone: "control",
    width: 42.4,
    x: 28.8,
    y: 10.5
  },
  {
    details: [
      "Browser TCP maps to HTTP/3 CONNECT.",
      "Browser UDP maps to target-specific CONNECT-UDP.",
      "Android opens the destination sockets from the device network."
    ],
    eyebrow: "Byte path",
    height: 34,
    id: "architecture-dataplane",
    label: "Dataplane",
    side: "top",
    summary: "Opaque traffic moves through the relay only after control-plane assignment is complete.",
    title: "Dataplane",
    tone: "data",
    width: 75.2,
    x: 12.4,
    y: 53.7
  },
  {
    details: [
      "Uses the configured SOCKS endpoint or gateway.",
      "Proxy-account modifiers become broker inputs.",
      "The customer never selects an Android device directly."
    ],
    eyebrow: "Entry point",
    height: 13.5,
    id: "architecture-customer",
    label: "Customer",
    side: "top",
    summary: "The outside request enters as ordinary proxy traffic.",
    title: "Customer",
    width: 17.4,
    x: 3.7,
    y: 67.9
  },
  {
    details: [
      "proxy-client launches Chrome behind a local SOCKS adapter.",
      "TUN mode captures proxy-bypassing browser flows.",
      "proxy-gateway brokers from the public SOCKS username."
    ],
    eyebrow: "Browser edge",
    height: 13.5,
    id: "architecture-browser-client",
    label: "Browser client",
    side: "top",
    summary: "The Linux edge normalizes browser or gateway traffic into browser-role MASQUE requests.",
    title: "Browser client / gateway",
    tone: "data",
    width: 17.4,
    x: 27.4,
    y: 67.9
  },
  {
    details: [
      "Validates relay participant headers and token.",
      "Allocates flow ids and asks Android to open matching flow streams.",
      "Forwards bytes and datagrams without owning target sockets."
    ],
    eyebrow: "Relay rendezvous",
    height: 13.5,
    id: "architecture-relay",
    label: "MASQUE relay",
    side: "top",
    summary: "The relay pairs browser flows with Android flow streams by sessionId.",
    title: "MASQUE relay",
    tone: "data",
    width: 17.4,
    x: 51.1,
    y: 67.9
  },
  {
    details: [
      "Keeps one outbound undetect-control stream open.",
      "Opens undetect-tcp or undetect-udp flow streams when requested.",
      "Stops accepting new flow metadata when charging or capacity policy blocks work."
    ],
    eyebrow: "Android node",
    height: 13.5,
    id: "architecture-android",
    label: "Android SDK",
    side: "top",
    summary: "The SDK owns Android-side relay participation and destination socket creation.",
    title: "Android SDK",
    tone: "warning",
    width: 17.4,
    x: 74.8,
    y: 67.9
  },
  {
    details: [
      "Reached by Android-owned TCP or UDP sockets.",
      "Worker and relay do not dial this destination.",
      "Responses return over the same paired relay path."
    ],
    eyebrow: "External destination",
    height: 13.5,
    id: "architecture-target",
    label: "Target network",
    side: "left",
    summary: "The destination sees traffic from the Android device network path.",
    title: "Target network",
    width: 17.4,
    x: 78.8,
    y: 19.1
  },
  {
    details: [
      "Hono routes handle sessions, broker assignments, node registration, events, and dashboard JSON.",
      "Credentialed non-local paths use the existing wrapped-message model.",
      "Runtime state is request-scoped and passed into actions."
    ],
    eyebrow: "Worker surface",
    height: 13.5,
    id: "architecture-worker-api",
    label: "Worker API",
    side: "bottom",
    summary: "The Worker owns the control-plane HTTP API, not the dataplane byte path.",
    title: "Worker API",
    tone: "control",
    width: 17.4,
    x: 33,
    y: 21.4
  },
  {
    details: [
      "KV is the runtime source for sessions, registrations, sticky state, and relay health.",
      "D1 mirrors queryable rows for dashboard and control-plane views.",
      "Dashboard records remain metadata and event projections."
    ],
    eyebrow: "Control records",
    height: 13.5,
    id: "architecture-records",
    label: "KV / D1",
    side: "bottom",
    summary: "Persistent records support assignment decisions and dashboard views.",
    title: "KV / D1 records",
    tone: "control",
    width: 13,
    x: 53.9,
    y: 21.4
  }
]

const transactionHotspots: DiagramHotspot[] = [
  {
    details: [
      "Can be local proxy-client traffic or a public proxy-gateway SOCKS control.",
      "The customer-facing endpoint hides the internal Android session.",
      "No target bytes enter the relay before assignment."
    ],
    eyebrow: "Step 1",
    height: 6,
    id: "transaction-proxy-request",
    label: "Proxy request",
    side: "bottom",
    summary: "The customer sends a normal proxy request to the browser-side edge.",
    title: "Proxy request",
    width: 17.3,
    x: 8.2,
    y: 17
  },
  {
    details: [
      "POST /v1/broker/assignments carries appId and parsed proxy-account modifiers.",
      "The Worker checks the dataplane control key and proxy account.",
      "Country, sticky session, strictness, and optional non-Android OS profile are metadata."
    ],
    eyebrow: "Step 2",
    height: 6,
    id: "transaction-broker-request",
    label: "Broker assignment",
    side: "bottom",
    summary: "The browser side asks the Worker which Android session and relay to join.",
    title: "Broker assignment request",
    tone: "control",
    width: 17.3,
    x: 25.5,
    y: 25
  },
  {
    details: [
      "The response includes sessionId, nodeId, nodeCategory, and relay transport.",
      "Selectable nodes must be active, accepting, and under capacity.",
      "Sticky strict requests fail instead of falling back when the sticky node is unavailable."
    ],
    eyebrow: "Step 3",
    height: 6,
    id: "transaction-broker-response",
    label: "Session id + relay",
    side: "top",
    summary: "The Worker returns the selected Android session and relay descriptor.",
    title: "Broker assignment response",
    tone: "control",
    width: 17.3,
    x: 25.9,
    y: 32.8
  },
  {
    details: [
      "SOCKS CONNECT opens a browser-role HTTP/3 CONNECT stream.",
      "SOCKS UDP ASSOCIATE lazily opens one CONNECT-UDP association per target.",
      "Requests carry session id, relay token, and participant id headers."
    ],
    eyebrow: "Step 4",
    height: 6,
    id: "transaction-connect",
    label: "CONNECT / CONNECT-UDP",
    side: "bottom",
    summary: "Browser-side proxy intent becomes MASQUE traffic toward the relay.",
    title: "Browser MASQUE open",
    tone: "data",
    width: 34.3,
    x: 25.5,
    y: 40.5
  },
  {
    details: [
      "Android keeps this outbound control stream open as role android.",
      "The relay writes length-prefixed JSON open metadata with kind, target, and flowId.",
      "If Android is not accepting clients, the native runtime skips new open metadata."
    ],
    eyebrow: "Step 5",
    height: 6,
    id: "transaction-android-control",
    label: "Android control stream",
    side: "top",
    summary: "The relay asks Android to open the matching flow for the browser request.",
    title: "Android control stream",
    tone: "warning",
    width: 17,
    x: 60.6,
    y: 48.5
  },
  {
    details: [
      "Android opens an undetect-tcp or undetect-udp flow stream back to the relay.",
      "The destination TCP or connected UDP socket is opened from Android.",
      "Target dial failures are reported back as android_target_rejected."
    ],
    eyebrow: "Step 6",
    height: 6,
    id: "transaction-target-socket",
    label: "Target socket",
    side: "top",
    summary: "Android owns the destination socket and completes relay pairing.",
    title: "Target socket",
    tone: "warning",
    width: 15.4,
    x: 77.6,
    y: 56.5
  },
  {
    details: [
      "Target bytes first return to Android.",
      "Android streams or datagrams them back over the paired relay flow.",
      "SDK events report channel type, byte/datagram counts, and close reason."
    ],
    eyebrow: "Step 7",
    height: 6,
    id: "transaction-return-android",
    label: "Response bytes",
    side: "top",
    summary: "Response traffic starts on the Android-owned target socket.",
    title: "Target response",
    tone: "data",
    width: 15.4,
    x: 77.6,
    y: 64.5
  },
  {
    details: [
      "The relay forwards opaque response payloads back to the browser MASQUE stream or datagram association.",
      "TCP bytes are pumped over paired streams.",
      "UDP payloads use HTTP datagrams or the repo-private datagram capsule fallback."
    ],
    eyebrow: "Step 8",
    height: 7,
    id: "transaction-return-relay",
    label: "Return dataplane",
    side: "top",
    summary: "The relay returns payloads to the browser side without inspecting them.",
    title: "Relay return path",
    tone: "data",
    width: 34.3,
    x: 25.9,
    y: 79.5
  },
  {
    details: [
      "The browser client or gateway writes the response to the original proxy flow.",
      "Control-plane records can update via redacted events.",
      "Packet contents are not included in dashboard records."
    ],
    eyebrow: "Step 9",
    height: 6,
    id: "transaction-return-customer",
    label: "Customer response",
    side: "top",
    summary: "The customer receives the response on the original connection.",
    title: "Customer response",
    tone: "data",
    width: 17.3,
    x: 8.6,
    y: 90
  }
]

const sessionTimeline: SessionTimelineItem[] = [
  {
    actor: "Android SDK",
    date: "01",
    description:
      "The host app starts the SDK. If charging policy allows work, the SDK creates a parent API session and receives short-lived credentials.",
    detail:
      "Session bootstrap uses POST /v1/sessions. Credentialed follow-up calls use the wrapped message shape; production bootstrap encryption is still a separate hardening path.",
    handoff: "An Android-owned session and credential envelope now exist.",
    icon: "i-ph-device-mobile",
    order: "1",
    title: "SDK creates the Android session",
    value: "sdk-session"
  },
  {
    actor: "Android SDK",
    date: "02",
    description:
      "The SDK registers node availability, capacity, and nodeCategory with the Worker, then starts the relay MASQUE runtime with the returned transport descriptor.",
    detail:
      "The descriptor is type relay, protocol masque, and includes host, port, and an optional relay token. Kotlin rejects unsupported transport types.",
    handoff: "The SDK opens the Android control stream to the relay.",
    icon: "i-ph-plugs-connected",
    order: "2",
    title: "Android joins the relay",
    value: "android-relay"
  },
  {
    actor: "Customer",
    date: "03",
    description:
      "The customer sends a proxy connection request through the configured local browser client or gateway.",
    detail: "The customer sees a normal proxy endpoint. The target bytes have not entered the relay yet.",
    handoff: "Customer intent becomes a local SOCKS or gateway request.",
    icon: "i-ph-user",
    order: "3",
    title: "Customer starts the proxy connection",
    value: "customer-request"
  },
  {
    actor: "Browser client",
    date: "04",
    description:
      "The browser side either uses an explicit session or asks the Worker broker for the selected Android session and relay transport.",
    detail:
      "Broker requests carry appId and parsed proxy-account modifiers such as country, sticky session, strict mode, and optional non-Android OS profile.",
    handoff: "The browser side learns which Android session to join.",
    icon: "i-ph-browser",
    order: "4",
    title: "Client asks for an assignment",
    value: "client-assignment"
  },
  {
    actor: "Local Chrome wrapper",
    date: "05",
    description:
      "proxy-client launches Chrome with a localhost SOCKS5 proxy by default. TUN mode instead runs Chrome inside a Linux user/network namespace and captures egress on udtun0.",
    detail:
      "The public proxy-gateway skips Chrome, brokers from the SOCKS5 username, and can reuse idle brokered MASQUE clients by assignment key.",
    handoff: "TCP or UDP proxy intent is normalized into browser-side MASQUE operations.",
    icon: "i-ph-browser",
    order: "5",
    title: "Browser-edge traffic is normalized",
    value: "chrome-encapsulation"
  },
  {
    actor: "Worker API",
    date: "06",
    description:
      "The Worker broker filters active node registrations by app, country, sticky affinity, strictness, accepting state, and capacity before returning the selected node.",
    detail:
      "KV is the runtime store. D1 mirrors records used by dashboard and control-plane query surfaces. Fresh relay health is logged as visibility evidence but selection is still registration based.",
    handoff: "The browser side receives the Android session id plus the configured relay rendezvous point.",
    icon: "i-ph-cloud",
    order: "6",
    title: "Broker chooses node and relay coordinates",
    value: "broker-selection"
  },
  {
    actor: "Dataplane relay",
    date: "07",
    description:
      "The browser MASQUE client opens HTTP/3 CONNECT for TCP or CONNECT-UDP for a target-specific UDP association.",
    detail:
      "Relay requests carry role, session id, token, and participant id headers. UDP datagrams use context id 0, with a private 0xffa5 capsule fallback for payloads that cannot fit the outer QUIC datagram.",
    handoff: "Opaque payload bytes enter relay handling.",
    icon: "i-ph-plugs-connected",
    order: "7",
    title: "Relay pairs the participants",
    value: "relay-pair"
  },
  {
    actor: "Android SDK",
    date: "08",
    description:
      "The relay sends length-prefixed JSON open metadata over the Android control stream. The Android runtime opens the target TCP or UDP socket and joins with an Android flow stream.",
    detail:
      "Android flow streams use repo-private extended CONNECT protocols: undetect-tcp or undetect-udp at /v1/transport/masque/android/flow.",
    handoff: "Target traffic is now handled by the Android network path.",
    icon: "i-ph-device-mobile",
    order: "8",
    title: "SDK opens the target connection",
    value: "sdk-target"
  },
  {
    actor: "Return path",
    date: "09",
    description:
      "Target responses return through the Android SDK, relay, and browser client to the customer's original connection.",
    detail:
      "TCP response bytes are pumped over the paired streams. UDP response payloads return as HTTP datagrams or the same private capsule shape.",
    handoff: "Customer receives the response on the original proxy flow.",
    icon: "i-ph-arrows-left-right",
    order: "9",
    title: "Response flows back through the dataplane",
    value: "return-path"
  },
  {
    actor: "Events",
    date: "10",
    description:
      "The SDK and control plane emit compact state events for session, client, credential, relay, and transport changes.",
    detail:
      "Native TCP and UDP flow events become SDK client connection events, then redacted dashboard events. Packet payloads and raw credentials are not included.",
    handoff: "Operators get state visibility without packet contents.",
    icon: "i-ph-activity",
    order: "10",
    title: "Structured events update the dashboard",
    value: "events"
  }
]

const readingLinks: ReadingLink[] = [
  {
    href: "https://www.rfc-editor.org/rfc/rfc9110.html#name-connect",
    label: "HTTP CONNECT",
    note: "The base HTTP tunnel method used for TCP-style proxy flows.",
    source: "RFC 9110"
  },
  {
    href: "https://www.rfc-editor.org/rfc/rfc9298.html",
    label: "CONNECT-UDP",
    note: "The MASQUE method for proxying UDP through HTTP.",
    source: "RFC 9298"
  },
  {
    href: "https://www.rfc-editor.org/rfc/rfc9000.html",
    label: "QUIC transport",
    note: "The transport protocol underneath HTTP/3 relay paths.",
    source: "RFC 9000"
  },
  {
    href: "https://developer.android.com/develop/connectivity/network-ops/connecting",
    label: "Android network sockets",
    note: "Android guidance for opening and managing app network connections.",
    source: "Android Developers"
  },
  {
    href: "https://developers.cloudflare.com/workers/",
    label: "Cloudflare Workers",
    note: "The serverless runtime that hosts the dashboard API surface.",
    source: "Cloudflare Docs"
  },
  {
    href: "https://developers.cloudflare.com/kv/",
    label: "Workers KV",
    note: "The low-latency key-value store used for runtime session records.",
    source: "Cloudflare Docs"
  },
  {
    href: "https://developers.cloudflare.com/d1/",
    label: "D1",
    note: "The SQL mirror used for dashboard and control-plane query surfaces.",
    source: "Cloudflare Docs"
  },
  {
    href: "https://www.rfc-editor.org/rfc/rfc9297.html",
    label: "HTTP Datagrams and capsules",
    note: "Datagram and capsule framing used by HTTP proxying extensions such as CONNECT-UDP.",
    source: "RFC 9297"
  }
]

const boundaryRows = [
  {
    label: "Worker API",
    owns: "Authentication, session records, Android node registration, broker assignment, credential refresh, dashboard data.",
    avoids: "Forwarding payload bytes or storing request bodies."
  },
  {
    label: "Browser client/gateway",
    owns: "SOCKS parsing, optional Chrome/TUN launch, broker assignment calls, browser-role MASQUE clients, gateway pooling.",
    avoids: "Android target sockets, relay pairing internals, or Worker session storage."
  },
  {
    label: "Relay",
    owns: "Relay-token validation, browser/Android flow pairing, opaque TCP byte and HTTP datagram forwarding, idle expiry.",
    avoids: "Parent API behavior, durable event storage, or policy decisions."
  },
  {
    label: "Android SDK",
    owns: "SDK-created target sockets, relay transport, credential refresh behavior, structured SDK events.",
    avoids: "VpnService routing, host-app traffic inspection, HTTPS fallback, or user-selected OS profiles."
  },
  {
    label: "Dashboard",
    owns: "Read-only operational views over sessions, nodes, events, relay state, and diagnostics.",
    avoids: "Secrets in URLs, payload inspection, or direct dataplane control."
  }
]

const transportDetails: TransportDetail[] = [
  {
    stage: "Broker assignment",
    flow: "When no explicit relay session is supplied, proxy-client or proxy-gateway calls POST /v1/broker/assignments with appId and parsed proxy-account modifiers.",
    implementation:
      "The Worker authorizes the control key and proxy account, filters active registrations by app and country, applies sticky/strict behavior, then returns the selected node sessionId and relay MASQUE host, port, and token when configured.",
    notes: [
      "Selectable means active, unexpired, acceptingClients, and activeClientCount below maxClientCount.",
      "Fresh relay health is kept as relay visibility evidence for logs and dashboard state; it does not forward traffic or replace node registration state."
    ]
  },
  {
    stage: "Default Chrome wrapper",
    flow: "proxy-client launches one Chrome/Chromium process with --proxy-server pointing at a localhost, no-auth SOCKS5 listener.",
    implementation:
      "SOCKS CONNECT calls the browser MASQUE client's OpenStream. SOCKS UDP ASSOCIATE creates a local UDP socket and lazily opens one CONNECT-UDP flow per target.",
    notes: [
      "Chrome QUIC remains enabled by default; --disable-quic is only for TCP-only smoke tests.",
      "The parent proxy-client owns the QUIC/HTTP/3 connection to the relay."
    ]
  },
  {
    stage: "Public SOCKS gateway",
    flow: "proxy-gateway accepts external SOCKS5 controls, converts the SOCKS username into the broker proxy URL, and joins the assigned relay as role browser.",
    implementation:
      "The gateway can reuse idle brokered MASQUE clients keyed by session id, relay, token, participant id, proxy URL, MASQUE URL, and TLS mode. The default idle TTL is intentionally below the Worker node registration TTL.",
    notes: [
      "The gateway keeps SOCKS5 parsing outside the relay process.",
      "TCP controls, UDP associations, handshakes, and idle UDP windows have explicit caps and timeouts."
    ]
  },
  {
    stage: "TUN encapsulation",
    flow: "With encapsulation mode tun, proxy-client forks a helper under unshare, maps the current user, creates a user/network/mount namespace, and launches Chrome inside it. netns is only a deprecated alias for this path.",
    implementation:
      "The helper creates udtun0, installs default IPv4/IPv6 routes to that TUN device, runs tun2socks, and forwards captured TCP/UDP flows over a Unix flow.sock bridge to the parent process.",
    notes: [
      "Proxy-aware traffic still uses the helper-local SOCKS listener.",
      "The parent process stays outside the namespace so relay and control-plane access remain host-owned."
    ]
  },
  {
    stage: "TCP target flow",
    flow: "Browser TCP becomes HTTP/3 CONNECT with the target host:port as authority and browser role/session/token headers.",
    implementation:
      "The relay allocates a flowId, writes length-prefixed JSON open metadata to Android's control stream, waits for Android to open an undetect-tcp flow stream, then pumps bytes between both streams.",
    notes: [
      "The relay never opens the target TCP socket.",
      "Android dials the target TCP socket and emits redacted tcp_stream connection events."
    ]
  },
  {
    stage: "UDP target flow",
    flow: "Browser UDP becomes a MASQUE CONNECT-UDP request at /.well-known/masque/udp/{host}/{port}/ for each target.",
    implementation:
      "Payloads use HTTP datagrams with context id 0. When the outer QUIC datagram is too small, browser, relay, and Android all support the repo-private 0xffa5 datagram capsule fallback on the same stream.",
    notes: [
      "Android opens a connected UDP socket with net.DialUDP for the single target carried by that CONNECT-UDP flow.",
      "This documents target-specific UDP associations, not a general relay-owned UDP socket."
    ]
  },
  {
    stage: "Android admission",
    flow: "Android keeps one outbound control stream open as role android and only accepts new flow metadata while SDK capacity policy allows work.",
    implementation:
      "Charging is the first admission gate. When charging stops with active clients, the SDK stops accepting new flows and marks the node as draining; existing flows are allowed to terminate.",
    notes: [
      "setAcceptingNewClients(false) causes new relay open metadata to be skipped by Android.",
      "The Worker broker uses stored acceptingClients, active counts, and capacityReason when selecting nodes."
    ]
  },
  {
    stage: "Relay cleanup",
    flow: "The relay validates relay headers, tracks Android participants and pending browser flows by sessionId, and forwards opaque stream bytes or datagram payloads.",
    implementation:
      "When a browser flow or Android stream closes, the paired stream is canceled. When Android disconnects, pending flow channels are closed and the participant is removed.",
    notes: [
      "Relay health counters describe streams, datagrams, drops, participants, and pair state.",
      "The relay is not the parent API, broker, event store, Chrome launcher, or target socket owner."
    ]
  }
]
</script>

<template>
  <DashboardLayout>
    <div class="grid gap-5">
      <PageHeader
        title="Architecture documentation"
        description="Current architecture and transport path."
        details="How Android nodes register and join the relay, how browser-edge traffic becomes MASQUE flows, how target sockets are opened, and how redacted state reaches the dashboard."
      >
        <template #actions>
          <DashboardBadge label="DOCS" tone="neutral" />
        </template>
      </PageHeader>

      <section class="grid gap-4">
        <ChartPanel title="System map" eyebrow="Control path and byte path">
          <div class="dashboard-doc-diagram-shell">
            <div class="dashboard-doc-diagram-stage">
              <svg
                class="dashboard-doc-diagram"
                viewBox="0 0 920 430"
                role="img"
                aria-labelledby="architecture-map-title architecture-map-desc"
              >
                <title id="architecture-map-title">Proxy architecture map</title>
                <desc id="architecture-map-desc">
                  The Android SDK registers with the Worker API and joins the relay. The browser client asks the Worker
                  API for an assignment, then browser and Android exchange proxy bytes through the relay while dashboard
                  records stay outside the dataplane.
                </desc>
                <defs>
                  <marker id="doc-arrow" markerHeight="9" markerWidth="9" orient="auto" refX="8" refY="4.5">
                    <path d="M0,0 L9,4.5 L0,9 Z" class="dashboard-doc-arrow-head" />
                  </marker>
                  <marker id="doc-arrow-muted" markerHeight="9" markerWidth="9" orient="auto" refX="8" refY="4.5">
                    <path d="M0,0 L9,4.5 L0,9 Z" class="dashboard-doc-arrow-head dashboard-doc-arrow-head--muted" />
                  </marker>
                </defs>

                <g class="dashboard-doc-zone">
                  <title>
                    Control-plane decisions: sessions, credentials, node registration, broker selection, relay health,
                    and dashboard projections.
                  </title>
                  <rect x="265" y="45" width="390" height="146" rx="2" />
                  <text x="287" y="77">Control plane</text>
                </g>
                <g class="dashboard-doc-zone dashboard-doc-zone--dataplane">
                  <title>
                    Dataplane traffic: browser-originated proxy bytes move through the relay to Android-owned target
                    sockets.
                  </title>
                  <rect x="114" y="231" width="692" height="146" rx="2" />
                  <text x="136" y="263">Dataplane</text>
                </g>

                <g class="dashboard-doc-node">
                  <title>
                    Customer traffic enters through the configured proxy endpoint; the customer does not pick an Android
                    device directly.
                  </title>
                  <rect x="34" y="292" width="160" height="58" rx="2" />
                  <text x="114" y="315" text-anchor="middle">Customer</text>
                  <text x="114" y="336" text-anchor="middle" class="dashboard-doc-node-caption">browser traffic</text>
                </g>
                <g class="dashboard-doc-node">
                  <title>
                    The Linux browser runner or public gateway normalizes SOCKS/TUN traffic into browser-side MASQUE
                    requests.
                  </title>
                  <rect x="252" y="292" width="160" height="58" rx="2" />
                  <text x="332" y="315" text-anchor="middle">Browser client</text>
                  <text x="332" y="336" text-anchor="middle" class="dashboard-doc-node-caption">SOCKS / TUN edge</text>
                </g>
                <g class="dashboard-doc-node dashboard-doc-node--strong">
                  <title>
                    The HTTP/3 MASQUE relay pairs browser and Android participants by sessionId and forwards opaque
                    bytes or datagrams.
                  </title>
                  <rect x="470" y="292" width="160" height="58" rx="2" />
                  <text x="550" y="315" text-anchor="middle">MASQUE relay</text>
                  <text x="550" y="336" text-anchor="middle" class="dashboard-doc-node-caption">
                    opaque byte pairing
                  </text>
                </g>
                <g class="dashboard-doc-node">
                  <title>
                    The Android SDK keeps the Android relay participant open and opens target TCP/UDP sockets from the
                    device network.
                  </title>
                  <rect x="688" y="292" width="160" height="58" rx="2" />
                  <text x="768" y="315" text-anchor="middle">Android SDK</text>
                  <text x="768" y="336" text-anchor="middle" class="dashboard-doc-node-caption">target sockets</text>
                </g>
                <g class="dashboard-doc-node">
                  <title>
                    The destination service is reached by Android-owned sockets; Worker and relay never open this target
                    connection.
                  </title>
                  <rect x="725" y="82" width="160" height="58" rx="2" />
                  <text x="805" y="105" text-anchor="middle">Target network</text>
                  <text x="805" y="126" text-anchor="middle" class="dashboard-doc-node-caption">
                    destination service
                  </text>
                </g>

                <g class="dashboard-doc-node dashboard-doc-node--control">
                  <title>
                    The Hono Worker handles sessions, credentials, node registration, broker assignment, events, relay
                    health, and dashboard data routes.
                  </title>
                  <rect x="304" y="92" width="160" height="58" rx="2" />
                  <text x="384" y="115" text-anchor="middle">Worker API</text>
                  <text x="384" y="136" text-anchor="middle" class="dashboard-doc-node-caption">session + broker</text>
                </g>
                <g class="dashboard-doc-node dashboard-doc-node--control">
                  <title>
                    KV stores runtime state. D1 mirrors queryable session, sticky, and event rows for dashboard and
                    control-plane views.
                  </title>
                  <rect x="496" y="92" width="120" height="58" rx="2" />
                  <text x="556" y="115" text-anchor="middle">KV / D1</text>
                  <text x="556" y="136" text-anchor="middle" class="dashboard-doc-node-caption">records</text>
                </g>

                <g>
                  <title>Customer proxy traffic reaches the local browser client or public SOCKS gateway first.</title>
                  <path d="M194 321 H248" class="dashboard-doc-flow" marker-end="url(#doc-arrow)" />
                </g>
                <g>
                  <title>
                    Browser TCP becomes HTTP/3 CONNECT and browser UDP becomes CONNECT-UDP toward the relay.
                  </title>
                  <path d="M412 321 H466" class="dashboard-doc-flow" marker-end="url(#doc-arrow)" />
                  <text x="441" y="284" class="dashboard-doc-edge-label" text-anchor="middle">
                    CONNECT / CONNECT-UDP bytes
                  </text>
                </g>
                <g>
                  <title>
                    The relay asks Android to open a matching flow stream, then forwards opaque payloads between paired
                    streams.
                  </title>
                  <path d="M630 321 H684" class="dashboard-doc-flow" marker-end="url(#doc-arrow)" />
                </g>
                <g>
                  <title>Android opens the target connection from the device network.</title>
                  <path d="M792 292 C820 237 822 190 808 144" class="dashboard-doc-flow" marker-end="url(#doc-arrow)" />
                </g>
                <g>
                  <title>
                    Response bytes return from the target through Android and the relay to the browser-side connection.
                  </title>
                  <path
                    d="M725 125 C675 166 654 238 626 290"
                    class="dashboard-doc-flow dashboard-doc-flow--return"
                    marker-end="url(#doc-arrow)"
                  />
                </g>
                <g>
                  <title>
                    The browser client or gateway asks the Worker broker for an assignment when no session is pinned.
                  </title>
                  <path
                    d="M332 292 C330 218 345 180 377 154"
                    class="dashboard-doc-flow dashboard-doc-flow--muted"
                    marker-end="url(#doc-arrow-muted)"
                  />
                  <text x="338" y="210" class="dashboard-doc-edge-label" text-anchor="middle">broker assignment</text>
                </g>
                <g>
                  <title>The Worker persists runtime records to KV and mirrors queryable state into D1.</title>
                  <path
                    d="M464 121 H492"
                    class="dashboard-doc-flow dashboard-doc-flow--muted"
                    marker-end="url(#doc-arrow-muted)"
                  />
                </g>
                <g>
                  <title>
                    Android session bootstrap, node registration, credential refresh, and redacted event uploads feed
                    Worker records and dashboard projections.
                  </title>
                  <path
                    d="M768 292 C724 222 664 158 620 126"
                    class="dashboard-doc-flow dashboard-doc-flow--muted"
                    marker-end="url(#doc-arrow-muted)"
                  />
                  <text x="685" y="210" class="dashboard-doc-edge-label" text-anchor="middle">
                    session, node + events
                  </text>
                </g>
              </svg>
              <div class="dashboard-doc-hotspot-layer" aria-label="Architecture diagram details">
                <div
                  v-for="hotspot in architectureHotspots"
                  :key="hotspot.id"
                  class="dashboard-doc-hotspot-popover"
                  :style="{
                    left: `${hotspot.x}%`,
                    top: `${hotspot.y}%`,
                    width: `${hotspot.width}%`,
                    height: `${hotspot.height}%`
                  }"
                >
                  <UPopover
                    mode="hover"
                    arrow
                    :open-delay="120"
                    :close-delay="160"
                    :content="{
                      align: hotspot.align ?? 'center',
                      collisionPadding: 16,
                      side: hotspot.side ?? 'top',
                      sideOffset: 10
                    }"
                    :ui="{ content: 'dashboard-doc-popover-content' }"
                  >
                    <button
                      type="button"
                      class="dashboard-doc-hotspot"
                      :data-tone="hotspot.tone ?? 'neutral'"
                      :aria-label="`${hotspot.title}: ${hotspot.summary}`"
                    >
                      <span class="dashboard-doc-hotspot__label">{{ hotspot.label }}</span>
                    </button>

                    <template #content>
                      <article class="dashboard-doc-popover-card" :data-tone="hotspot.tone ?? 'neutral'">
                        <p class="dashboard-label">{{ hotspot.eyebrow }}</p>
                        <p class="mt-2 dashboard-mono text-sm text-(--dash-ink)">{{ hotspot.title }}</p>
                        <p class="mt-3 text-sm leading-6 text-(--dash-muted)">{{ hotspot.summary }}</p>
                        <ul class="mt-3 grid gap-2">
                          <li v-for="detail in hotspot.details" :key="detail" class="dashboard-doc-popover-note">
                            {{ detail }}
                          </li>
                        </ul>
                      </article>
                    </template>
                  </UPopover>
                </div>
              </div>
            </div>
          </div>
        </ChartPanel>
      </section>

      <section class="grid gap-4 xl:grid-cols-[minmax(0,1.15fr)_minmax(340px,0.85fr)]">
        <ChartPanel title="Transaction view" eyebrow="Requests, assignments, and return traffic">
          <div class="dashboard-doc-diagram-shell">
            <div class="dashboard-doc-diagram-stage">
              <svg
                class="dashboard-doc-diagram"
                viewBox="0 0 980 500"
                role="img"
                aria-labelledby="transaction-view-title transaction-view-desc"
              >
                <title id="transaction-view-title">Typical session transaction view</title>
                <desc id="transaction-view-desc">
                  A sequence view from customer request through Worker assignment, relay pairing, Android target access,
                  and the return path.
                </desc>
                <defs>
                  <marker id="doc-seq-arrow" markerHeight="8" markerWidth="8" orient="auto" refX="7" refY="4">
                    <path d="M0,0 L8,4 L0,8 Z" class="dashboard-doc-arrow-head" />
                  </marker>
                </defs>

                <g class="dashboard-doc-lane">
                  <title>
                    Customer opens a normal proxy connection; the rest of the path is hidden behind the configured proxy
                    endpoint.
                  </title>
                  <text x="80" y="40" text-anchor="middle">Customer</text>
                  <line x1="80" y1="60" x2="80" y2="448" />
                </g>
                <g class="dashboard-doc-lane">
                  <title>Browser client or gateway parses proxy intent and owns the browser-side MASQUE client.</title>
                  <text x="250" y="40" text-anchor="middle">Browser client</text>
                  <line x1="250" y1="60" x2="250" y2="448" />
                </g>
                <g class="dashboard-doc-lane">
                  <title>
                    Worker API authenticates broker requests, stores session records, and returns selected Android
                    session plus relay transport.
                  </title>
                  <text x="420" y="40" text-anchor="middle">Worker API</text>
                  <line x1="420" y1="60" x2="420" y2="448" />
                </g>
                <g class="dashboard-doc-lane">
                  <title>
                    Relay validates participant headers, pairs flow streams by sessionId, and forwards opaque
                    bytes/datagrams.
                  </title>
                  <text x="590" y="40" text-anchor="middle">Relay</text>
                  <line x1="590" y1="60" x2="590" y2="448" />
                </g>
                <g class="dashboard-doc-lane">
                  <title>
                    Android SDK keeps the Android control stream open and dials target TCP/UDP sockets from Android.
                  </title>
                  <text x="760" y="40" text-anchor="middle">Android SDK</text>
                  <line x1="760" y1="60" x2="760" y2="448" />
                </g>
                <g class="dashboard-doc-lane">
                  <title>External destination reached by Android-owned target sockets.</title>
                  <text x="910" y="40" text-anchor="middle">Target</text>
                  <line x1="910" y1="60" x2="910" y2="448" />
                </g>

                <g>
                  <title>The customer sends browser traffic to the configured SOCKS endpoint or gateway.</title>
                  <path d="M80 96 H246" class="dashboard-doc-flow" marker-end="url(#doc-seq-arrow)" />
                  <text x="164" y="86" class="dashboard-doc-edge-label" text-anchor="middle">proxy request</text>
                </g>
                <g>
                  <title>
                    The browser side calls POST /v1/broker/assignments with app, country, strictness, and sticky inputs.
                  </title>
                  <path
                    d="M250 136 H416"
                    class="dashboard-doc-flow dashboard-doc-flow--muted"
                    marker-end="url(#doc-seq-arrow)"
                  />
                  <text x="333" y="126" class="dashboard-doc-edge-label" text-anchor="middle">broker assignment</text>
                </g>
                <g>
                  <title>
                    The Worker returns the selected Android sessionId, nodeId, nodeCategory, and relay MASQUE transport.
                  </title>
                  <path
                    d="M420 176 H254"
                    class="dashboard-doc-flow dashboard-doc-flow--muted"
                    marker-end="url(#doc-seq-arrow)"
                  />
                  <text x="337" y="166" class="dashboard-doc-edge-label" text-anchor="middle">session id + relay</text>
                </g>
                <g>
                  <title>
                    Browser TCP opens HTTP/3 CONNECT; browser UDP opens CONNECT-UDP for a target-specific association.
                  </title>
                  <path d="M250 216 H586" class="dashboard-doc-flow" marker-end="url(#doc-seq-arrow)" />
                  <text x="418" y="206" class="dashboard-doc-edge-label" text-anchor="middle">
                    CONNECT / CONNECT-UDP
                  </text>
                </g>
                <g>
                  <title>
                    Android maintains the undetect-control stream; the relay sends length-prefixed open metadata on it.
                  </title>
                  <path d="M760 256 H594" class="dashboard-doc-flow" marker-end="url(#doc-seq-arrow)" />
                  <text x="677" y="246" class="dashboard-doc-edge-label" text-anchor="middle">
                    android control stream
                  </text>
                </g>
                <g>
                  <title>
                    Android replies with an undetect-tcp or undetect-udp flow stream and owns the destination socket.
                  </title>
                  <path d="M760 296 H906" class="dashboard-doc-flow" marker-end="url(#doc-seq-arrow)" />
                  <text x="833" y="286" class="dashboard-doc-edge-label" text-anchor="middle">target socket</text>
                </g>
                <g>
                  <title>Target response bytes first return to Android over the Android-owned target socket.</title>
                  <path
                    d="M910 336 H764"
                    class="dashboard-doc-flow dashboard-doc-flow--return"
                    marker-end="url(#doc-seq-arrow)"
                  />
                  <text x="837" y="326" class="dashboard-doc-edge-label" text-anchor="middle">response bytes</text>
                </g>
                <g>
                  <title>Android streams the response back through its paired relay flow.</title>
                  <path
                    d="M760 376 H594"
                    class="dashboard-doc-flow dashboard-doc-flow--return"
                    marker-end="url(#doc-seq-arrow)"
                  />
                </g>
                <g>
                  <title>
                    The relay forwards the opaque response back to the browser-side MASQUE stream or datagram
                    association.
                  </title>
                  <path
                    d="M590 416 H254"
                    class="dashboard-doc-flow dashboard-doc-flow--return"
                    marker-end="url(#doc-seq-arrow)"
                  />
                  <text x="420" y="406" class="dashboard-doc-edge-label" text-anchor="middle">
                    return through dataplane
                  </text>
                </g>
                <g>
                  <title>The browser client writes the response back to the original customer proxy flow.</title>
                  <path
                    d="M250 456 H84"
                    class="dashboard-doc-flow dashboard-doc-flow--return"
                    marker-end="url(#doc-seq-arrow)"
                  />
                </g>
              </svg>
              <div class="dashboard-doc-hotspot-layer" aria-label="Transaction diagram details">
                <div
                  v-for="hotspot in transactionHotspots"
                  :key="hotspot.id"
                  class="dashboard-doc-hotspot-popover"
                  :style="{
                    left: `${hotspot.x}%`,
                    top: `${hotspot.y}%`,
                    width: `${hotspot.width}%`,
                    height: `${hotspot.height}%`
                  }"
                >
                  <UPopover
                    mode="hover"
                    arrow
                    :open-delay="120"
                    :close-delay="160"
                    :content="{
                      align: hotspot.align ?? 'center',
                      collisionPadding: 16,
                      side: hotspot.side ?? 'top',
                      sideOffset: 10
                    }"
                    :ui="{ content: 'dashboard-doc-popover-content' }"
                  >
                    <button
                      type="button"
                      class="dashboard-doc-hotspot"
                      :data-tone="hotspot.tone ?? 'neutral'"
                      :aria-label="`${hotspot.title}: ${hotspot.summary}`"
                    >
                      <span class="dashboard-doc-hotspot__label">{{ hotspot.label }}</span>
                    </button>

                    <template #content>
                      <article class="dashboard-doc-popover-card" :data-tone="hotspot.tone ?? 'neutral'">
                        <p class="dashboard-label">{{ hotspot.eyebrow }}</p>
                        <p class="mt-2 dashboard-mono text-sm text-(--dash-ink)">{{ hotspot.title }}</p>
                        <p class="mt-3 text-sm leading-6 text-(--dash-muted)">{{ hotspot.summary }}</p>
                        <ul class="mt-3 grid gap-2">
                          <li v-for="detail in hotspot.details" :key="detail" class="dashboard-doc-popover-note">
                            {{ detail }}
                          </li>
                        </ul>
                      </article>
                    </template>
                  </UPopover>
                </div>
              </div>
            </div>
          </div>
        </ChartPanel>

        <DashboardCard title="What to read first" eyebrow="Core model" static>
          <div class="grid gap-4">
            <p class="text-sm leading-6 text-(--dash-muted)">
              Separate the system into decisions and bytes. The Worker API records the Android session, node capacity,
              broker assignment, relay endpoint, and dashboard projection. The dataplane carries browser bytes only
              after those decisions are made.
            </p>
            <div class="grid gap-3">
              <DocumentationCallout :icon="PhBracketsCurly" title="Control path">
                <p>Small JSON messages for auth, sessions, node registration, broker assignments, and event upload.</p>
              </DocumentationCallout>
              <DocumentationCallout :icon="PhPlugsConnected" title="Byte path">
                <p>HTTP/3 CONNECT streams and CONNECT-UDP associations forwarded through the relay to Android.</p>
              </DocumentationCallout>
              <DocumentationCallout :icon="PhDatabase" title="Records">
                <p>KV keeps runtime state. D1 mirrors queryable state for dashboard and control-plane views.</p>
              </DocumentationCallout>
            </div>
          </div>
        </DashboardCard>
      </section>

      <section class="grid gap-4 lg:grid-cols-2 2xl:grid-cols-3">
        <DashboardCard
          v-for="concept in concepts"
          :key="concept.title"
          :title="concept.title"
          :variant="concept.tone === 'warning' ? 'warning' : concept.tone === 'danger' ? 'danger' : 'dark'"
          static
        >
          <template v-if="concept.icon" #actions>
            <component :is="concept.icon" :size="18" class="text-(--dash-dim)" />
          </template>
          <p class="text-sm leading-6 text-(--dash-muted)">{{ concept.body }}</p>
          <ul class="mt-4 grid gap-2">
            <li v-for="detail in concept.details" :key="detail" class="dashboard-doc-list-item">
              {{ detail }}
            </li>
          </ul>
        </DashboardCard>
      </section>

      <DashboardCard title="Current transport steps" eyebrow="Implementation-backed path" static>
        <div class="dashboard-doc-step-grid">
          <article v-for="step in currentTransportSteps" :key="step.order" class="dashboard-doc-step">
            <div class="dashboard-doc-step__index">{{ step.order }}</div>
            <div class="dashboard-doc-step__body">
              <div class="flex min-w-0 flex-wrap items-baseline justify-between gap-2">
                <p class="dashboard-mono text-sm text-(--dash-ink)">{{ step.title }}</p>
                <span class="dashboard-label">{{ step.actor }}</span>
              </div>
              <div class="dashboard-doc-step__facts">
                <div class="dashboard-doc-step__fact">
                  <span>Control</span>
                  <p>{{ step.control }}</p>
                </div>
                <div class="dashboard-doc-step__fact">
                  <span>Bytes</span>
                  <p>{{ step.bytes }}</p>
                </div>
                <div class="dashboard-doc-step__fact">
                  <span>Evidence</span>
                  <p>{{ step.evidence }}</p>
                </div>
              </div>
            </div>
          </article>
        </div>
      </DashboardCard>

      <section class="grid gap-4">
        <DashboardCard title="Typical session" eyebrow="Sequential handoff" static>
          <UTimeline
            :items="sessionTimeline"
            color="neutral"
            size="sm"
            class="dashboard-doc-timeline"
            :ui="{
              root: 'gap-0',
              item: 'gap-4 pb-5 last:pb-0',
              container: 'pt-1',
              indicator: 'border border-(--dash-line-strong) bg-[rgba(241,243,242,0.045)] text-(--dash-ink)',
              separator: 'bg-(--dash-line)',
              date: 'dashboard-mono text-(--dash-dim)',
              title: 'text-(--dash-ink)',
              description: 'text-(--dash-muted)'
            }"
          >
            <template #indicator="{ item }">
              <span class="dashboard-mono text-[0.64rem]">{{ item.order }}</span>
            </template>
            <template #description="{ item }">
              <div class="grid gap-2 text-sm leading-6 text-(--dash-muted)">
                <p>{{ item.description }}</p>
                <p class="dashboard-doc-handoff">{{ item.handoff }}</p>
                <p class="text-xs leading-5 text-(--dash-dim)">{{ item.detail }}</p>
              </div>
            </template>
          </UTimeline>
        </DashboardCard>
      </section>

      <DashboardCard title="Transport details" eyebrow="Verified from current implementation" static>
        <div class="grid gap-3">
          <article v-for="item in transportDetails" :key="item.stage" class="dashboard-doc-boundary-row">
            <div>
              <p class="dashboard-mono text-sm text-(--dash-ink)">{{ item.stage }}</p>
            </div>
            <div>
              <p class="dashboard-label">Flow</p>
              <p class="mt-2 text-sm leading-6 text-(--dash-muted)">{{ item.flow }}</p>
            </div>
            <div>
              <p class="dashboard-label">Implementation</p>
              <p class="mt-2 text-sm leading-6 text-(--dash-muted)">{{ item.implementation }}</p>
              <ul class="mt-3 grid gap-2">
                <li v-for="note in item.notes" :key="note" class="dashboard-doc-list-item">
                  {{ note }}
                </li>
              </ul>
            </div>
          </article>
        </div>
      </DashboardCard>

      <section class="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(320px,0.55fr)]">
        <DashboardCard title="Ownership boundaries" eyebrow="What each part is allowed to do" static>
          <div class="grid gap-3">
            <article v-for="row in boundaryRows" :key="row.label" class="dashboard-doc-boundary-row">
              <div>
                <p class="dashboard-mono text-sm text-(--dash-ink)">{{ row.label }}</p>
              </div>
              <div>
                <p class="dashboard-label">Owns</p>
                <p class="mt-2 text-sm leading-6 text-(--dash-muted)">{{ row.owns }}</p>
              </div>
              <div>
                <p class="dashboard-label">Does not own</p>
                <p class="mt-2 text-sm leading-6 text-(--dash-muted)">{{ row.avoids }}</p>
              </div>
            </article>
          </div>
        </DashboardCard>

        <DashboardCard title="Current constraints" eyebrow="Design guardrails" static>
          <div class="grid gap-4">
            <DocumentationCallout :icon="PhWarning" title="Relay only" tone="warning">
              <p>
                No P2P NAT traversal, Android-owned listener ports, or ADB node-port forwarding in the active model.
              </p>
            </DocumentationCallout>
            <DocumentationCallout :icon="PhDeviceMobile" title="Android behavior">
              <p>
                Android SDK nodes use native Android socket behavior. Desktop proxy OS profiles are API/client metadata.
              </p>
            </DocumentationCallout>
            <DocumentationCallout :icon="PhGraph" title="Dashboard scope">
              <p>The dashboard explains state and failures. It should not expose secrets or packet contents.</p>
            </DocumentationCallout>
            <DocumentationCallout :icon="PhCloud" title="Relay credentials" tone="warning">
              <p>
                Staging still uses a relay token in the transport descriptor. Session-scoped relay credentials remain
                production hardening work.
              </p>
            </DocumentationCallout>
          </div>
        </DashboardCard>
      </section>

      <DashboardCard title="External reading" eyebrow="Deeper context" static>
        <div class="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <a
            v-for="link in readingLinks"
            :key="link.href"
            :href="link.href"
            target="_blank"
            rel="noreferrer"
            class="dashboard-focus dashboard-doc-reading-link"
          >
            <span class="flex min-w-0 items-start justify-between gap-3">
              <span class="min-w-0">
                <span class="dashboard-label">{{ link.source }}</span>
                <span class="mt-2 block text-sm font-medium text-(--dash-ink)">{{ link.label }}</span>
              </span>
              <PhArrowSquareOut :size="16" class="mt-0.5 shrink-0 text-(--dash-dim)" />
            </span>
            <span class="mt-3 block text-sm leading-5 text-(--dash-muted)">{{ link.note }}</span>
          </a>
        </div>
      </DashboardCard>
    </div>
  </DashboardLayout>
</template>
